"use strict";
/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { CANCELED, uncancelableToken } from "#src/util/cancellation.js";
import { Uint64 } from "#src/util/uint64.js";
export class HttpError extends Error {
  constructor(url, status, statusText, response) {
    let message = `Fetching ${JSON.stringify(
      url
    )} resulted in HTTP error ${status}`;
    if (statusText) {
      message += `: ${statusText}`;
    }
    message += ".";
    super(message);
    this.name = "HttpError";
    this.message = message;
    this.url = url;
    this.status = status;
    this.statusText = statusText;
    if (response) {
      this.response = response;
    }
  }
  static fromResponse(response) {
    return new HttpError(
      response.url,
      response.status,
      response.statusText,
      response
    );
  }
  static fromRequestError(input, error) {
    if (error instanceof TypeError) {
      let url;
      if (typeof input === "string") {
        url = input;
      } else {
        url = input.url;
      }
      return new HttpError(url, 0, "Network or CORS error");
    }
    return error;
  }
}
const maxAttempts = 32;
const minDelayMilliseconds = 500;
const maxDelayMilliseconds = 1e4;
export function pickDelay(attemptNumber) {
  return Math.min(
    2 ** attemptNumber * minDelayMilliseconds,
    maxDelayMilliseconds / 2
  ) * (1 + Math.random());
}
export async function fetchOk(input, init) {
  for (let requestAttempt = 0; ; ) {
    if (init?.signal?.aborted) {
      throw CANCELED;
    }
    let response;
    try {
      response = await fetch(input, init);
    } catch (error) {
      throw HttpError.fromRequestError(input, error);
    }
    if (!response.ok) {
      const { status } = response;
      if (status === 429 || status === 503 || status === 504) {
        if (++requestAttempt !== maxAttempts) {
          await new Promise(
            (resolve) => setTimeout(resolve, pickDelay(requestAttempt - 1))
          );
          continue;
        }
      }
      throw HttpError.fromResponse(response);
    }
    return response;
  }
}
export function responseArrayBuffer(response) {
  return response.arrayBuffer();
}
export function responseJson(response) {
  return response.json();
}
export async function cancellableFetchOk(input, init, transformResponse, cancellationToken = uncancelableToken) {
  if (cancellationToken === uncancelableToken) {
    const response = await fetchOk(input, init);
    return await transformResponse(response);
  }
  const abortController = new AbortController();
  const unregisterCancellation = cancellationToken.add(
    () => abortController.abort()
  );
  try {
    const response = await fetchOk(input, {
      ...init,
      signal: abortController.signal
    });
    return await transformResponse(response);
  } finally {
    unregisterCancellation();
  }
}
const tempUint64 = new Uint64();
export function getByteRangeHeader(startOffset, endOffset) {
  let endOffsetStr;
  if (typeof endOffset === "number") {
    endOffsetStr = `${endOffset - 1}`;
  } else {
    Uint64.decrement(tempUint64, endOffset);
    endOffsetStr = tempUint64.toString();
  }
  return { Range: `bytes=${startOffset}-${endOffsetStr}` };
}
export function parseUrl(url) {
  const urlProtocolPattern = /^([^:/]+):\/\/([^/]+)((?:\/.*)?)$/;
  const match = url.match(urlProtocolPattern);
  if (match === null) {
    throw new Error(`Invalid URL: ${JSON.stringify(url)}`);
  }
  return { protocol: match[1], host: match[2], path: match[3] };
}
export function isNotFoundError(e) {
  if (!(e instanceof HttpError)) return false;
  return e.status === 0 || e.status === 403 || e.status === 404;
}
